import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  CalendarClock,
  Car,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  DollarSign,
  Eye,
  FileCheck2,
  FileSpreadsheet,
  Filter,
  Gavel,
  GitCompare,
  Globe2,
  Heart,
  Image as ImageIcon,
  LayoutDashboard,
  LockKeyhole,
  Menu,
  MessageSquare,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Timer,
  Truck,
  Upload,
  User,
  UserCheck,
  UserX,
  X,
  Zap
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { bidHistory, filters, metrics, vehicles } from "./data/catalog";
import { countdown, currency, maskVin, number } from "./lib/format";
import type { Vehicle } from "./types";

type View = "home" | "marketplace" | "vehicle" | "dashboard" | "private-admin" | "admin-auth" | "auth" | "pending" | "not-found";
type AuthRole = "admin" | "customer";
type UserStatus = "pending" | "active" | "suspended" | "rejected";
type AuthUser = {
  id: string;
  email: string;
  phone?: string;
  fullName: string;
  country: string;
  role: AuthRole;
  status: UserStatus;
  verificationStatus: string;
  createdAt?: string;
};
type AuthState = { phase: "checking" | "ready"; user: AuthUser | null; message?: string };
type AdminUser = AuthUser & { createdAt: string };

const navItems = [
  ["marketplace", "Categories"],
  ["marketplace", "Live Auctions"],
  ["marketplace", "Buy Now"],
  ["home", "How it works"],
  ["dashboard", "Financing"],
  ["dashboard", "Support"]
] as const;

const authHeaders = () => ({ "Content-Type": "application/json" });

async function apiRequest<T>(url: string, init: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 12000);
  const response = await fetch(url, {
    ...init,
    credentials: "include",
    signal: init.signal || controller.signal,
    headers: { ...authHeaders(), ...(init.headers || {}) }
  }).finally(() => window.clearTimeout(timeout));
  const contentType = response.headers.get("content-type") || "";
  if (url.startsWith("/api/") && !contentType.includes("application/json")) throw new Error("Secure API is unavailable. Please try again shortly.");
  const body = (contentType.includes("application/json") ? await response.json() : {}) as { error?: string; message?: string };
  if (!response.ok) throw new Error(body.error || body.message || `Request failed with status ${response.status}`);
  return body as T;
}

function routeTo(path: string) {
  if (window.location.pathname === path && !window.location.hash) return;
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function normalizeRoute(): { view: View; id?: string } {
  const hash = window.location.hash.replace("#", "");
  const legacy = hash || window.location.pathname.replace(/^\/+/, "") || "home";
  const clean = legacy === "" ? "home" : legacy;
  if (clean.startsWith("vehicle/")) return { view: "vehicle", id: decodeURIComponent(clean.split("/")[1] || "") };
  if (clean === "secure-admin-login") return { view: "admin-auth" };
  if (clean === "private-admin") return { view: "private-admin" };
  if (["home", "marketplace", "dashboard", "auth", "pending"].includes(clean)) return { view: clean as View };
  return { view: "not-found" };
}

function useSpaRoute() {
  const [route, setRoute] = useState(normalizeRoute());
  useEffect(() => {
    const onRoute = () => setRoute(normalizeRoute());
    window.addEventListener("hashchange", onRoute);
    window.addEventListener("popstate", onRoute);
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const url = new URL(anchor.href);
      if (url.origin !== window.location.origin) return;
      const path = url.hash ? `/${url.hash.replace("#", "")}` : url.pathname;
      if (!path || path === "#") return;
      event.preventDefault();
      routeTo(path);
    };
    document.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("hashchange", onRoute);
      window.removeEventListener("popstate", onRoute);
      document.removeEventListener("click", onClick);
    };
  }, []);
  return route;
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="topbar">
      <a className="brand" href="#home" aria-label="Velox Auctions home">
        <span className="brand-mark"><Car size={21} /></span>
        <span><strong>Velox</strong><small>Global Auto Auctions</small></span>
      </a>
      <div className="nav-search">
        <Search size={18} />
        <input placeholder="Search VIN, lot, make, model, location..." />
        <button>Advanced</button>
      </div>
      <nav className="desktop-nav">
        {navItems.map(([href, label]) => <a key={label} href={`#${href}`}>{label}</a>)}
      </nav>
      <div className="nav-actions">
        <button className="chip"><Globe2 size={16} /> EN</button>
        <button className="chip"><DollarSign size={16} /> USD</button>
        <a className="ghost-btn" href="#auth">Sign in</a>
        <a className="solid-btn" href="#auth">Register</a>
        <button className="icon-btn mobile-only" onClick={() => setOpen(true)} aria-label="Open menu"><Menu /></button>
      </div>
      {open && (
        <div className="drawer-backdrop" onClick={() => setOpen(false)}>
          <aside className="mobile-drawer" onClick={(event) => event.stopPropagation()}>
            <button className="icon-btn close" onClick={() => setOpen(false)}><X /></button>
            <div className="brand drawer-brand"><span className="brand-mark"><Car /></span><span><strong>Velox</strong><small>Buyer console</small></span></div>
            {navItems.map(([href, label]) => <a key={label} href={`#${href}`} onClick={() => setOpen(false)}>{label}</a>)}
            <a href="#dashboard" onClick={() => setOpen(false)}>Client dashboard</a>
            <a href="#auth" onClick={() => setOpen(false)}>Sign in</a>
            <a href="#auth" onClick={() => setOpen(false)}>Register</a>
          </aside>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-media" />
      <div className="hero-content">
        <p className="eyebrow"><Zap size={16} /> Edge-powered global vehicle auctions</p>
        <h1>Velox Auctions</h1>
        <p className="hero-copy">A premium marketplace for live salvage, fleet, dealer, luxury, and export vehicle auctions with verified bidding, escrow, shipping, and compliance workflows.</p>
        <div className="hero-search">
          <Search />
          <input placeholder="Try: 2023 BMW M4, clean title, Miami, lot FL-9348201" />
          <a href="#marketplace">Search inventory</a>
        </div>
        <div className="hero-stats">
          <span><strong>24,880</strong> vehicles live</span>
          <span><strong>$2.4B</strong> annual GMV</span>
          <span><strong>96</strong> countries served</span>
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <main>
      <Hero />
      <section className="ticker-band">
        {["Reserve met on BMW M4", "Tesla Plaid ending in 16m", "Escrow deposits updated", "Dealer KYC SLA 8m", "Shipping lanes repriced"].map((item) => <span key={item}>{item}</span>)}
      </section>
      <SectionHeader eyebrow="Live auction floor" title="High-velocity auctions with verified bid activity" action="View all live" href="#marketplace" />
      <VehicleRail items={vehicles.slice(0, 3)} />
      <SectionHeader eyebrow="Featured inventory" title="Curated vehicles with strong buyer signal" action="Compare vehicles" href="#marketplace" />
      <VehicleGrid items={vehicles} compact={false} />
      <section className="split-section">
        <div>
          <p className="eyebrow">How it works</p>
          <h2>Search, inspect, bid, pay, ship.</h2>
          <div className="steps">
            {["Create a verified buyer profile with deposit controls.", "Save searches, inspect reports, compare lots, and model landed cost.", "Place live bids or max autobids with anti-sniping protection.", "Settle invoice, escrow, documents, and shipping from one dashboard."].map((step, index) => (
              <article key={step}><strong>{index + 1}</strong><p>{step}</p></article>
            ))}
          </div>
        </div>
        <TrustPanel />
      </section>
      <section className="category-band">
        {["Luxury", "Electric", "Commercial", "Run & Drive", "Clean Title", "Export Ready", "Fleet", "Parts Only"].map((cat) => <a href="#marketplace" key={cat}>{cat}</a>)}
      </section>
      <section className="analytics-strip">
        {metrics.map((metric) => <MetricCard key={metric.label} metric={metric} />)}
      </section>
      <section className="testimonial-newsletter">
        <div className="testimonial">
          <Star />
          <blockquote>Velox feels like a trading desk for vehicles. Our buyers see bid risk, title status, transport cost, and compliance status without leaving the auction.</blockquote>
          <p>Marina Cole, Director of Remarketing</p>
        </div>
        <form className="newsletter">
          <h3>Market brief</h3>
          <p>Weekly auction intelligence, title trends, and lane pricing.</p>
          <input placeholder="operations@company.com" />
          <button type="button">Subscribe</button>
        </form>
      </section>
    </main>
  );
}

function SectionHeader({ eyebrow, title, action, href }: { eyebrow: string; title: string; action: string; href: string }) {
  return (
    <div className="section-header">
      <div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></div>
      <a href={href}>{action}</a>
    </div>
  );
}

function VehicleRail({ items }: { items: Vehicle[] }) {
  return <section className="vehicle-rail">{items.map((vehicle) => <VehicleCard key={vehicle.id} vehicle={vehicle} featured />)}</section>;
}

function VehicleGrid({ items, compact }: { items: Vehicle[]; compact?: boolean }) {
  return <section className={compact ? "vehicle-grid compact" : "vehicle-grid"}>{items.map((vehicle) => <VehicleCard key={vehicle.id} vehicle={vehicle} />)}</section>;
}

function VehicleCard({ vehicle, featured }: { vehicle: Vehicle; featured?: boolean }) {
  return (
    <article className={featured ? "vehicle-card featured" : "vehicle-card"}>
      <a className="card-image" href={`#vehicle/${vehicle.id}`}>
        <img src={vehicle.images[0]} alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`} loading="lazy" />
        <span className={`status ${vehicle.auctionStatus}`}>{vehicle.auctionStatus.replace("-", " ")}</span>
        <span className="timer"><Timer size={15} /> {countdown(vehicle.endsIn)}</span>
      </a>
      <div className="card-body">
        <div className="card-title-row">
          <div>
            <h3>{vehicle.year} {vehicle.make} {vehicle.model}</h3>
            <p>{vehicle.trim}</p>
          </div>
          <button className="icon-btn"><Heart size={18} /></button>
        </div>
        <div className="bid-row">
          <span><small>Current bid</small><strong>{currency(vehicle.currentBid)}</strong></span>
          <span><small>Buy now</small><strong>{currency(vehicle.buyNow)}</strong></span>
        </div>
        <dl className="spec-grid">
          <div><dt>Mileage</dt><dd>{number(vehicle.mileage)} mi</dd></div>
          <div><dt>Location</dt><dd>{vehicle.location}</dd></div>
          <div><dt>Damage</dt><dd>{vehicle.primaryDamage}</dd></div>
          <div><dt>Lot</dt><dd>{vehicle.lot}</dd></div>
        </dl>
        <div className="card-actions">
          <span>{maskVin(vehicle.vin)}</span>
          <button><GitCompare size={16} /> Compare</button>
          <a href={`#vehicle/${vehicle.id}`}><Eye size={16} /> Quick view</a>
        </div>
      </div>
    </article>
  );
}

function Marketplace() {
  const [query, setQuery] = useState("");
  const [make, setMake] = useState("All");
  const [damage, setDamage] = useState("All");
  const [sort, setSort] = useState("Ending soon");
  const [drawer, setDrawer] = useState(false);
  const [page, setPage] = useState(1);
  const result = useMemo(() => {
    let list = vehicles.filter((vehicle) => {
      const haystack = `${vehicle.make} ${vehicle.model} ${vehicle.trim} ${vehicle.vin} ${vehicle.lot} ${vehicle.location}`.toLowerCase();
      return haystack.includes(query.toLowerCase()) && (make === "All" || vehicle.make === make) && (damage === "All" || vehicle.primaryDamage === damage);
    });
    if (sort === "Highest bid") list = [...list].sort((a, b) => b.currentBid - a.currentBid);
    if (sort === "Lowest mileage") list = [...list].sort((a, b) => a.mileage - b.mileage);
    if (sort === "Ending soon") list = [...list].sort((a, b) => a.endsIn - b.endsIn);
    return list;
  }, [query, make, damage, sort]);

  return (
    <main className="marketplace-layout" id="marketplace">
      <button className="floating-filter" onClick={() => setDrawer(true)}><Filter /> Filters</button>
      <aside className="filter-panel desktop-filter"><Filters make={make} damage={damage} setMake={setMake} setDamage={setDamage} /></aside>
      {drawer && <div className="drawer-backdrop" onClick={() => setDrawer(false)}><aside className="filter-panel mobile-filter" onClick={(event) => event.stopPropagation()}><button className="icon-btn close" onClick={() => setDrawer(false)}><X /></button><Filters make={make} damage={damage} setMake={setMake} setDamage={setDamage} /></aside></div>}
      <section className="market-results">
        <div className="market-toolbar">
          <div>
            <p className="eyebrow">Marketplace</p>
            <h1>Search {number(24880)} active vehicle auctions</h1>
          </div>
          <div className="toolbar-controls">
            <div className="input-pill"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="VIN, lot, make, location..." /></div>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>{["Ending soon", "Highest bid", "Lowest mileage"].map((item) => <option key={item}>{item}</option>)}</select>
            <button><Bell size={17} /> Save search</button>
          </div>
        </div>
        <div className="saved-row">
          {["BMW clean title", "Electric under $70k", "Run & Drive Florida", "Export SUVs"].map((saved) => <button key={saved}>{saved}</button>)}
        </div>
        <VehicleGrid items={result} compact />
        <div className="pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span>Page {page} of 12</span>
          <button onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </section>
    </main>
  );
}

function Filters({ make, damage, setMake, setDamage }: { make: string; damage: string; setMake: (v: string) => void; setDamage: (v: string) => void }) {
  return (
    <>
      <div className="filter-title"><SlidersHorizontal /><div><h2>Advanced filters</h2><p>Instant inventory matching</p></div></div>
      <FilterSelect label="Make" value={make} setValue={setMake} options={["All", ...filters.makes]} />
      <FilterSelect label="Damage" value={damage} setValue={setDamage} options={["All", ...filters.damages]} />
      <FilterSelect label="Location" value="All" setValue={() => null} options={["All", ...filters.locations]} />
      <FilterSelect label="Condition" value="All" setValue={() => null} options={["All", ...filters.conditions]} />
      <FilterSelect label="Title status" value="All" setValue={() => null} options={["All", ...filters.titleStatuses]} />
      <div className="range-card"><span>Year</span><div><input defaultValue="2018" /><input defaultValue="2026" /></div></div>
      <div className="range-card"><span>Price range</span><div><input defaultValue="$5,000" /><input defaultValue="$150,000" /></div></div>
      <div className="check-list">
        {["Live auctions only", "Reserve met", "Keys available", "Clean title", "Buy now enabled"].map((item) => <label key={item}><input type="checkbox" /> {item}</label>)}
      </div>
    </>
  );
}

function FilterSelect({ label, value, setValue, options }: { label: string; value: string; setValue: (v: string) => void; options: string[] }) {
  return <label className="filter-field"><span>{label}</span><select value={value} onChange={(event) => setValue(event.target.value)}>{options.map((item) => <option key={item}>{item}</option>)}</select></label>;
}

function VehicleDetail({ vehicle }: { vehicle: Vehicle }) {
  const [bid, setBid] = useState(vehicle.currentBid + 250);
  const [confirm, setConfirm] = useState(false);
  const [image, setImage] = useState(vehicle.images[0] || "");
  const [liveBid, setLiveBid] = useState(vehicle.currentBid);
  useEffect(() => {
    const timer = window.setInterval(() => setLiveBid((current) => current + (Math.random() > 0.65 ? 250 : 0)), 4500);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    setBid(vehicle.currentBid + 250);
    setImage(vehicle.images[0] || "");
    setLiveBid(vehicle.currentBid);
  }, [vehicle.id, vehicle.currentBid, vehicle.images]);

  return (
    <main className="detail-page">
      <section className="gallery-panel">
        <div className="main-photo">
          {image ? <img src={image} alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`} /> : <div className="vehicle-skeleton">Loading vehicle media...</div>}
          <button><ImageIcon /> Fullscreen</button>
        </div>
        <div className="thumb-row">{vehicle.images.map((src) => <button key={src} onClick={() => setImage(src)}><img src={src} alt="" /></button>)}</div>
      </section>
      <aside className="bid-panel">
        <p className="eyebrow">{vehicle.auctionType} / {vehicle.lot}</p>
        <h1>{vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}</h1>
        <div className="vin-row"><span>VIN {vehicle.vin}</span><span>{vehicle.titleStatus}</span></div>
        <div className="countdown-box"><Timer /><div><span>Ends in</span><strong>{countdown(vehicle.endsIn)}</strong></div><small>{vehicle.auctionDate}</small></div>
        <div className="detail-money"><span><small>Current bid</small><strong>{currency(liveBid)}</strong></span><span><small>Buy now</small><strong>{currency(vehicle.buyNow)}</strong></span></div>
        <div className="bid-controls">
          <label>Max bid<input type="number" value={bid} onChange={(event) => setBid(Number(event.target.value))} /></label>
          <button onClick={() => setConfirm(true)}><Gavel /> Place bid</button>
          <button className="secondary"><Heart /> Watchlist</button>
        </div>
        <div className="reserve">{vehicle.reserveMet ? "Reserve met" : "Reserve not met"} · Increment {currency(250)} · Anti-sniping enabled</div>
        <div className="sticky-mobile-bid"><button onClick={() => setConfirm(true)}><Gavel /> Bid {currency(bid)}</button></div>
      </aside>
      <section className="detail-content">
        <InfoGrid vehicle={vehicle} />
        <DamagePanel vehicle={vehicle} />
        <BidHistory liveBid={liveBid} />
        <FinanceShip vehicle={vehicle} />
        <SectionHeader eyebrow="Recommended" title="Similar vehicles for your watchlist" action="Open marketplace" href="#marketplace" />
        <VehicleGrid items={vehicles.filter((item) => item.id !== vehicle.id).slice(0, 3)} compact />
      </section>
      {confirm && <BidModal bid={bid} vehicle={vehicle} close={() => setConfirm(false)} />}
    </main>
  );
}

function InfoGrid({ vehicle }: { vehicle: Vehicle }) {
  const rows = [
    ["Seller", vehicle.seller], ["Location", vehicle.location], ["Condition", vehicle.condition], ["Keys", vehicle.specs.keys ? "Available" : "Missing"],
    ["Odometer", `${number(vehicle.mileage)} mi`], ["Engine", vehicle.specs.engine], ["Transmission", vehicle.specs.transmission], ["Fuel", vehicle.specs.fuel],
    ["Drivetrain", vehicle.specs.drivetrain], ["Color", vehicle.specs.color], ["Body", vehicle.specs.body], ["Interior", vehicle.specs.interior]
  ];
  return <section className="info-grid">{rows.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</section>;
}

function DamagePanel({ vehicle }: { vehicle: Vehicle }) {
  return (
    <section className="panel-row">
      <article className="rich-panel"><h2>Damage & inspection</h2><dl><div><dt>Primary</dt><dd>{vehicle.primaryDamage}</dd></div><div><dt>Secondary</dt><dd>{vehicle.secondaryDamage}</dd></div><div><dt>Repair estimate</dt><dd>{currency(vehicle.repairEstimate)}</dd></div></dl><p>{vehicle.inspectionNotes}</p></article>
      <article className="rich-panel"><h2>Vehicle history</h2><p>Title events, odometer consistency, seller disclosure, inspection media, and ownership timeline are consolidated for compliance review.</p><button><FileCheck2 /> Request full report</button></article>
      <article className="rich-panel"><h2>Seller profile</h2><p>{vehicle.seller} has a 98.7% document completion score, 4.8 day average release time, and verified inventory source.</p><button><ShieldCheck /> View seller</button></article>
    </section>
  );
}

function BidHistory({ liveBid }: { liveBid: number }) {
  return (
    <section className="live-table">
      <div className="table-head"><h2>Live bid history</h2><span><Activity size={16} /> Live updates</span></div>
      <table><thead><tr><th>Bidder</th><th>Amount</th><th>Source</th><th>Time</th></tr></thead><tbody>{bidHistory.map((bid, index) => <tr key={bid.id}><td>{bid.bidder}</td><td>{currency(index === 0 ? liveBid : bid.amount)}</td><td>{bid.source}</td><td>{bid.time}</td></tr>)}</tbody></table>
    </section>
  );
}

function FinanceShip({ vehicle }: { vehicle: Vehicle }) {
  return <section className="finance-grid"><article><CreditCard /><span>Estimated financing</span><strong>{currency(vehicle.financingMonthly)}/mo</strong><p>Based on 20% down, buyer verification, and market APR.</p></article><article><Truck /><span>Shipping estimate</span><strong>{currency(vehicle.shippingEstimate)}</strong><p>Insured open carrier to a commercial delivery point.</p></article><article><Bell /><span>Notifications</span><strong>Outbid + ending alerts</strong><p>Email, push, SMS, and in-app delivery preferences.</p></article></section>;
}

function BidModal({ vehicle, bid, close }: { vehicle: Vehicle; bid: number; close: () => void }) {
  return (
    <div className="modal-backdrop" onClick={close}>
      <section className="bid-modal" onClick={(event) => event.stopPropagation()}>
        <button className="icon-btn close" onClick={close}><X /></button>
        <h2>Confirm bid</h2>
        <p>{vehicle.year} {vehicle.make} {vehicle.model} · Lot {vehicle.lot}</p>
        <div className="confirm-price">{currency(bid)}</div>
        <label><input type="checkbox" /> I understand buyer fees, deposit hold, title status, and auction terms.</label>
        <button onClick={close}><LockKeyhole /> Submit secure bid</button>
      </section>
    </div>
  );
}

function Dashboard() {
  const [wallet, setWallet] = useState<{ balance_cents: number; frozen_cents: number; status: string } | null>(null);
  const [invoices, setInvoices] = useState<Array<{ id: string; reference: string; status: string; amount_cents: number; expires_at: string }>>([]);
  const [notifications, setNotifications] = useState<Array<{ id: string; title: string; body: string; type: string; created_at: string }>>([]);

  useEffect(() => {
    let alive = true;
    const loadDashboardData = async () => {
      try {
        const [walletResult, invoiceResult, notificationResult] = await Promise.all([
          apiRequest<{ wallet: { balance_cents: number; frozen_cents: number; status: string } }>("/api/wallet"),
          apiRequest<{ data: Array<{ id: string; reference: string; status: string; amount_cents: number; expires_at: string }> }>("/api/invoices"),
          apiRequest<{ data: Array<{ id: string; title: string; body: string; type: string; created_at: string }> }>("/api/notifications")
        ]);
        if (!alive) return;
        setWallet(walletResult.wallet);
        setInvoices(invoiceResult.data);
        setNotifications(notificationResult.data);
      } catch {
        if (!alive) return;
      }
    };
    void loadDashboardData();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const approval = notifications.find((item) => item.type === "account_approved");
    if (!approval) return;
    void playNotification(approval.title, approval.body);
  }, [notifications]);

  return (
    <main className="dashboard-shell">
      <Sidebar title="Buyer dashboard" items={["Overview", "Active bids", "Won vehicles", "Watchlist", "Saved searches", "Invoices", "Shipping", "Documents", "Verification", "Support"]} />
      <section className="dashboard-main">
        <div className="dash-title"><div><p className="eyebrow">Client console</p><h1>Portfolio overview</h1></div><button><MessageSquare /> New support ticket</button></div>
        <section className="analytics-strip">{metrics.map((metric) => <MetricCard key={metric.label} metric={metric} />)}</section>
        <section className="finance-grid">
          <article><CreditCard /><span>Escrow balance</span><strong>{currency((wallet?.balance_cents || 0) / 100)}</strong><p>Balances are controlled by operations after invoice payment confirmation.</p></article>
          <article><FileCheck2 /><span>Open invoices</span><strong>{invoices.filter((invoice) => ["pending", "sent", "viewed"].includes(invoice.status)).length}</strong><p>Review payment instructions, timers, references, and proof uploads.</p></article>
          <article><Bell /><span>Notifications</span><strong>{notifications.length}</strong><p>Account, invoice, escrow, bidding, and auction alerts.</p></article>
        </section>
        <section className="dashboard-grid">
          <article className="chart-card"><h2>Bid exposure</h2><div className="bars">{[42, 70, 55, 88, 62, 96, 74].map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}</div></article>
          <article className="timeline-card"><h2>Activity timeline</h2>{(notifications.length ? notifications.map((item) => item.title) : ["Outbid on Tesla Model S", "Invoice VX-64018 ready", "Shipping quote updated", "KYC document approved"]).map((item) => <p key={item}><span />{item}</p>)}</article>
          <article className="watch-card"><h2>Auction tracking</h2>{vehicles.slice(0, 4).map((vehicle) => <a href={`#vehicle/${vehicle.id}`} key={vehicle.id}><strong>{vehicle.make} {vehicle.model}</strong><span>{currency(vehicle.currentBid)} · {countdown(vehicle.endsIn)}</span></a>)}</article>
        </section>
      </section>
    </main>
  );
}

async function playNotification(title: string, body: string) {
  try {
    if ("Notification" in window) {
      const permission = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
      if (permission === "granted") new Notification(title, { body });
    }
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    gain.gain.setValueAtTime(0.001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.45);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.48);
  } catch {
    // Browser, OS, and device notification policies are respected.
  }
}

function Admin({ auth }: { auth: AuthState }) {
  return (
    <main className="dashboard-shell admin-shell">
      <Sidebar title="Operations control" items={["Dashboard", "Users", "Pending Approvals", "Escrow Management", "Invoices", "Auctions", "Vehicles", "Bids", "Notifications", "CSV Imports", "Analytics", "Activity Logs", "Settings"]} />
      <section className="dashboard-main">
        <div className="dash-title"><div><p className="eyebrow">Marketplace command center</p><h1>Operations dashboard</h1></div><button><Upload /> Create auction</button></div>
        <section className="analytics-strip">
          {[...metrics, { label: "Escrow balances", value: "$8.4M", delta: "manual controls", tone: "neutral" as const }].slice(0, 4).map((metric) => <MetricCard key={metric.label} metric={metric} />)}
        </section>
        <PendingUsersPanel auth={auth} />
        <section className="admin-grid">
          <EscrowInvoicePanel />
          <ImportPanel />
          <article className="admin-table"><div className="table-head"><h2>Vehicle management</h2><button>Bulk actions <ChevronDown size={15} /></button></div><table><thead><tr><th>Lot</th><th>Vehicle</th><th>Status</th><th>Bid</th><th>Actions</th></tr></thead><tbody>{vehicles.map((vehicle) => <tr key={vehicle.id}><td>{vehicle.lot}</td><td>{vehicle.year} {vehicle.make} {vehicle.model}</td><td>{vehicle.auctionStatus}</td><td>{currency(vehicle.currentBid)}</td><td><button>Edit</button><button>Feature</button></td></tr>)}</tbody></table></article>
          <article className="chart-card"><h2>Revenue analytics</h2><div className="bars wide">{[64, 74, 58, 91, 86, 77, 99, 88, 93].map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}</div></article>
          <article className="timeline-card"><h2>Admin activity feed</h2>{["CSV import validated 218 rows", "Auction VX-23844 extended by anti-snipe", "Seller Atlantic Financial verified", "Homepage luxury rail updated"].map((item) => <p key={item}><span />{item}</p>)}</article>
        </section>
      </section>
    </main>
  );
}

function PendingUsersPanel({ auth }: { auth: AuthState }) {
  const [pendingUsers, setPendingUsers] = useState<AdminUser[]>([]);
  const [activeUsers, setActiveUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionUser, setActionUser] = useState<string | null>(null);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  const loadUsers = async () => {
    if (auth.user?.role !== "admin" || auth.user.status !== "active") return;
    setLoading(true);
    setError("");
    try {
      const [pending, active] = await Promise.all([
        apiRequest<{ data: AdminUser[] }>("/api/admin/users?status=pending"),
        apiRequest<{ data: AdminUser[] }>("/api/admin/users?status=active")
      ]);
      setPendingUsers(pending.data);
      setActiveUsers(active.data.filter((user) => user.role !== "admin"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, [auth.user?.id, auth.user?.role, auth.user?.status]);

  const updateStatus = async (userId: string, action: "approve" | "reject" | "suspend") => {
    setActionUser(userId);
    setToast(action === "approve" ? "approving..." : `${action}ing...`);
    setError("");
    console.log("APPROVAL: admin approval request", { userId, action });
    try {
      const result = await apiRequest<{ user: AdminUser; message: string }>(`/api/admin/users/${userId}/${action}`, { method: "POST" });
      console.log("APPROVAL: DB update result", result.user);
      setToast(result.message || (action === "approve" ? "User approved successfully" : `User ${action}ed`));
      await loadUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Approval failed.";
      setError(message);
      setToast(`failed approval: ${message}`);
    } finally {
      setActionUser(null);
    }
  };

  return (
    <article className="admin-table approval-table">
      <div className="table-head">
        <div><h2>Pending user approvals</h2><p>Database-backed approval queue. UI refreshes only from backend responses.</p></div>
        <button onClick={loadUsers}><Activity size={15} /> Sync DB</button>
      </div>
      {toast && <div className={toast.startsWith("failed") ? "auth-message error" : "auth-message success"}>{toast}</div>}
      {error && <div className="auth-message error">{error}</div>}
      {loading ? (
        <div className="auth-message">checking account approvals...</div>
      ) : (
        <table>
          <thead><tr><th>User</th><th>Email</th><th>Phone</th><th>Country</th><th>Registered</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {pendingUsers.length === 0 && <tr><td colSpan={7}>No pending approvals in the database.</td></tr>}
            {pendingUsers.map((user) => (
              <tr key={user.id}>
                <td><span className="avatar">{user.fullName.split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase()}</span>{user.fullName}</td>
                <td>{user.email}</td>
                <td>{user.phone || "Not supplied"}</td>
                <td>{user.country}</td>
                <td>{user.createdAt ? new Date(user.createdAt).toLocaleString() : "Recorded"}</td>
                <td><StatusBadge status={user.status} /></td>
                <td>
                  <button disabled={actionUser === user.id} onClick={() => updateStatus(user.id, "approve")}><UserCheck size={15} /> {actionUser === user.id ? "Approving..." : "Approve"}</button>
                  <button disabled={actionUser === user.id} onClick={() => updateStatus(user.id, "reject")}><UserX size={15} /> Reject</button>
                  <button disabled={actionUser === user.id} onClick={() => updateStatus(user.id, "suspend")}><AlertTriangle size={15} /> Suspend</button>
                  <button>View details</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="active-users-note"><CheckCircle2 size={16} /> Active customer accounts: {activeUsers.length}</div>
    </article>
  );
}

function StatusBadge({ status }: { status: UserStatus }) {
  return <span className={`status-badge ${status}`}>{status}</span>;
}

function ImportPanel() {
  return (
    <article className="import-panel">
      <div><FileSpreadsheet /><h2>Scraped data import</h2><p>Upload JSON or CSV, auto-map fields, validate VINs, persist vehicle records, and prepare image galleries for publishing.</p></div>
      <div className="dropzone"><Upload /><strong>Drop CSV/JSON here</strong><span>VIN, lot, images, price, damage, location, title, seller, auction date, specs</span></div>
      <div className="mapping-grid">{["VIN", "Lot number", "Images", "Auction date", "Mileage", "Damage", "Seller", "Location"].map((field) => <label key={field}><span>{field}</span><select><option>Auto mapped</option><option>Ignore</option></select></label>)}</div>
      <button>Preview import</button>
    </article>
  );
}

function EscrowInvoicePanel() {
  const [amount, setAmount] = useState("5000");
  const [userId, setUserId] = useState("");
  const [message, setMessage] = useState("");
  const createInvoice = async () => {
    setMessage("Creating invoice...");
    try {
      await apiRequest("/api/admin/invoices", {
        method: "POST",
        body: JSON.stringify({
          userId,
          amountCents: Math.round(Number(amount) * 100),
          bankName: "Velox Settlement Bank",
          accountHolder: "Velox Auctions Escrow",
          paymentInstructions: "Send wire payment using the invoice reference. Upload proof in the customer invoice page.",
          expiresMinutes: 120
        })
      });
      setMessage("Invoice sent and customer notified.");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Invoice failed.");
    }
  };
  return (
    <article className="import-panel">
      <div><CreditCard /><h2>Escrow & invoice control</h2><p>Manual operations workflow for funding requests, invoice creation, external payment confirmation, wallet credits, deductions, and freezes.</p></div>
      <div className="mapping-grid">
        <label><span>Customer ID</span><input value={userId} onChange={(event) => setUserId(event.target.value)} placeholder="user id" /></label>
        <label><span>Amount</span><input value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="5000" /></label>
        <label><span>Bank name</span><input defaultValue="Velox Settlement Bank" /></label>
        <label><span>SWIFT / Network</span><input defaultValue="VELOXUS33" /></label>
      </div>
      {message && <div className="auth-message">{message}</div>}
      <button onClick={createInvoice}>Create invoice</button>
    </article>
  );
}

function Sidebar({ title, items }: { title: string; items: string[] }) {
  return <aside className="side-nav"><h2>{title}</h2>{items.map((item, index) => <a href="#" key={item} className={index === 0 ? "active" : ""}>{index === 0 ? <LayoutDashboard /> : <Settings />}{item}</a>)}</aside>;
}

function AuthFlow({ auth, onAuth }: { auth: AuthState; onAuth: (next: AuthState) => void }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+1 305 555 0194");
  const [fullName, setFullName] = useState("Verified Buyer");
  const [country, setCountry] = useState("United States");
  const [password, setPassword] = useState("");
  const [phase, setPhase] = useState<"idle" | "checking" | "waiting" | "success">("idle");
  const [message, setMessage] = useState(auth.message || "");

  const submit = async () => {
    setPhase("checking");
    setMessage(mode === "login" ? "checking account..." : "creating account...");
    console.log("AUTH: login attempt", { email, mode });
    try {
      if (mode === "register") {
        const result = await apiRequest<{ user: AuthUser; message: string }>("/api/auth/signup", {
          method: "POST",
          body: JSON.stringify({ email, phone, fullName, country, password })
        });
        setPhase("waiting");
        setMessage(result.message || "Registration successful. Awaiting admin approval.");
        onAuth({ phase: "ready", user: null, message: result.message });
        return;
      }
      const result = await apiRequest<{ user: AuthUser; message: string }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password })
      });
      console.log("AUTH: role assigned", { role: result.user.role, status: result.user.status });
      setPhase("success");
      setMessage("login successful");
      onAuth({ phase: "ready", user: result.user, message: "login successful" });
      routeTo("/dashboard");
    } catch (err) {
      const text = err instanceof Error ? err.message : "Authentication failed.";
      setPhase(text.toLowerCase().includes("approval") ? "waiting" : "idle");
      setMessage(text);
      onAuth({ phase: "ready", user: null, message: text });
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card">
        <p className="eyebrow">Secure access</p>
        <h1>Buyer verification portal</h1>
        <div className="auth-tabs">
          <button className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>Sign in</button>
          <button className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>Register</button>
          <button type="button">KYC</button>
        </div>
        {message && <div className={phase === "success" ? "auth-message success" : phase === "waiting" ? "auth-message pending" : "auth-message"}>{message}</div>}
        {mode === "register" && <label>Full name<input value={fullName} onChange={(event) => setFullName(event.target.value)} placeholder="Verified Buyer" /></label>}
        <label>Email<input value={email} onChange={(event) => setEmail(event.target.value)} placeholder="buyer@company.com" /></label>
        <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Minimum 8 characters" /></label>
        <div className="auth-grid"><label>Country<select value={country} onChange={(event) => setCountry(event.target.value)}><option>United States</option><option>Canada</option><option>Nigeria</option><option>Germany</option></select></label><label>Phone / OTP<input value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+1 305 555 0194" /></label></div>
        <label className="terms"><input type="checkbox" /> Remember this verified device</label>
        <button onClick={submit} disabled={phase === "checking"}><ShieldCheck /> {phase === "checking" ? (mode === "login" ? "Verifying account..." : "Submitting registration...") : mode === "login" ? "Continue securely" : "Submit registration"}</button>
        <p>Captcha, OTP, deposit checks, account approval, and secure session validation protect the buyer experience.</p>
      </section>
    </main>
  );
}

function AdminAuthFlow({ onAuth }: { onAuth: (next: AuthState) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phase, setPhase] = useState<"idle" | "checking" | "success">("idle");
  const [message, setMessage] = useState("");

  const submit = async () => {
    setPhase("checking");
    setMessage("Verifying secure operator account...");
    try {
      const result = await apiRequest<{ user: AuthUser; message: string }>("/api/admin/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password })
      });
      setPhase("success");
      setMessage(result.message || "admin login successful");
      onAuth({ phase: "ready", user: result.user, message: result.message });
      routeTo("/private-admin");
    } catch (err) {
      setPhase("idle");
      setMessage(err instanceof Error ? err.message : "Secure login failed.");
      onAuth({ phase: "ready", user: null, message: err instanceof Error ? err.message : "Secure login failed." });
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card">
        <p className="eyebrow">Restricted operations access</p>
        <h1>Secure operator sign in</h1>
        {message && <div className={phase === "success" ? "auth-message success" : "auth-message"}>{message}</div>}
        <label>Email<input value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        <button onClick={submit} disabled={phase === "checking"}><LockKeyhole /> {phase === "checking" ? "Verifying..." : "Enter operations console"}</button>
      </section>
    </main>
  );
}

function ProtectedRoute({ auth, role, children }: { auth: AuthState; role: AuthRole; children: ReactNode }) {
  useEffect(() => {
    console.log("ROUTING: route access attempt", { requiredRole: role, phase: auth.phase, userRole: auth.user?.role, status: auth.user?.status });
    if (auth.phase === "ready" && (!auth.user || auth.user.status !== "active")) {
      console.log("ROUTING: role check result", { allowed: false, reason: "not_authenticated_or_inactive" });
      routeTo("/auth");
    }
    if (auth.phase === "ready" && auth.user && auth.user.status === "active" && auth.user.role !== role) {
      console.log("ROUTING: role check result", { allowed: false, requiredRole: role, userRole: auth.user.role });
      routeTo("/auth");
    }
  }, [auth.phase, auth.user?.id, auth.user?.role, auth.user?.status, role]);

  if (auth.phase === "checking") return <SystemState title="Checking account..." body="Validating your session against the database." />;
  if (!auth.user) return <SystemState title="Login required" body="Redirecting to secure sign in." />;
  if (auth.user.status !== "active") return <SystemState title="Account pending approval" body="Your account is awaiting admin approval." />;
  if (auth.user.role !== role) return <SystemState title="Access restricted" body="This route is protected by database role checks." />;
  console.log("ROUTING: role check result", { allowed: true, requiredRole: role, userRole: auth.user.role });
  return <>{children}</>;
}

function AdminProtectedRoute({ auth, children }: { auth: AuthState; children: ReactNode }) {
  useEffect(() => {
    if (auth.phase === "ready" && (!auth.user || auth.user.role !== "admin" || auth.user.status !== "active")) routeTo("/secure-admin-login");
  }, [auth.phase, auth.user?.id, auth.user?.role, auth.user?.status]);
  if (auth.phase === "checking") return <SystemState title="Checking secure access..." body="Validating the operator session." />;
  if (!auth.user || auth.user.role !== "admin" || auth.user.status !== "active") return <SystemState title="Restricted access" body="Secure operator login is required." />;
  return <>{children}</>;
}

function SystemState({ title, body }: { title: string; body: string }) {
  return (
    <main className="auth-page">
      <section className="auth-card">
        <p className="eyebrow">Secure routing</p>
        <h1>{title}</h1>
        <p>{body}</p>
      </section>
    </main>
  );
}

function VehicleRoute({ vehicle }: { vehicle?: Vehicle }) {
  if (!vehicle) {
    return (
      <main className="detail-page">
        <section className="rich-panel route-error">
          <AlertTriangle />
          <h1>Vehicle not found</h1>
          <p>The requested lot ID does not match an active vehicle record. The app stayed stable and did not reset session state.</p>
          <a className="solid-btn" href="#marketplace">Return to marketplace</a>
        </section>
      </main>
    );
  }
  return <VehicleDetail vehicle={vehicle} />;
}

function TrustPanel() {
  return <aside className="trust-panel"><ShieldCheck /><h3>Enterprise trust layer</h3><p>Verified buyers, protected account approval, secure escrow operations, KYC workflows, audit trails, and anti-fraud controls support every transaction.</p><div><span>99.99%</span><span>availability target</span></div></aside>;
}

function MetricCard({ metric }: { metric: { label: string; value: string; delta: string; tone: string } }) {
  return <article className={`metric-card ${metric.tone}`}><span>{metric.label}</span><strong>{metric.value}</strong><small>{metric.delta}</small></article>;
}

function BottomNav() {
  return <nav className="bottom-nav"><a href="#home"><Car /> Home</a><a href="#marketplace"><Search /> Search</a><a href="#vehicle/VX-23844"><Gavel /> Bid</a><a href="#dashboard"><User /> Account</a></nav>;
}

function Footer() {
  const groups = [
    { title: "Marketplace", links: [["Live Auctions", "#marketplace"], ["Buy Now", "#marketplace"], ["Locations", "#marketplace"], ["Vehicle Finder", "#marketplace"]] },
    { title: "Support", links: [["Help Center", "#auth"], ["Contact Us", "#auth"], ["Shipping", "#dashboard"], ["How To Buy", "#home"]] },
    { title: "Corporate", links: [["About Us", "#home"], ["Careers", "#home"], ["Terms", "#home"], ["Privacy", "#home"]] },
    { title: "Legal", links: [["Compliance", "#home"], ["AML Policy", "#home"], ["KYC Policy", "#auth"]] }
  ];
  return (
    <footer className="footer corporate-footer">
      <div><strong>Velox Auctions</strong><p>Verified vehicle auctions, escrow-supported settlement, logistics coordination, and buyer protection for global automotive commerce.</p></div>
      <div className="footer-groups">
        {groups.map((group) => (
          <section key={group.title}>
            <h3>{group.title}</h3>
            {group.links.map(([label, href]) => <a key={label} href={href}>{label}</a>)}
          </section>
        ))}
      </div>
    </footer>
  );
}

export default function App() {
  const route = useSpaRoute();
  const [auth, setAuth] = useState<AuthState>({ phase: "checking", user: null });
  const [adminAuth, setAdminAuth] = useState<AuthState>({ phase: "checking", user: null });
  const selected = route.view === "vehicle" ? vehicles.find((vehicle) => vehicle.id === route.id) : vehicles[0];

  useEffect(() => {
    let alive = true;
    const validateSession = async () => {
      console.log("AUTH: /auth/me session validation started");
      try {
        const result = await apiRequest<{ user: AuthUser | null }>("/api/auth/me", { method: "GET" });
        if (!alive) return;
        if (result.user?.status !== "active") {
          setAuth({ phase: "ready", user: null, message: result.user ? "Your account is awaiting admin approval." : undefined });
          if (result.user) routeTo("/pending");
          return;
        }
        setAuth({ phase: "ready", user: result.user });
      } catch (err) {
        if (!alive) return;
        setAuth({ phase: "ready", user: null, message: err instanceof Error ? err.message : "Session check failed." });
      }
    };
    void validateSession();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const validateAdminSession = async () => {
      try {
        const result = await apiRequest<{ user: AuthUser | null }>("/api/admin/auth/me", { method: "GET" });
        if (!alive) return;
        setAdminAuth({ phase: "ready", user: result.user });
      } catch {
        if (!alive) return;
        setAdminAuth({ phase: "ready", user: null });
      }
    };
    void validateAdminSession();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    document.title = route.view === "vehicle" && selected ? `${selected.year} ${selected.make} ${selected.model} | Velox Auctions` : "Velox Auctions | Enterprise Vehicle Auction Marketplace";
  }, [route.view, selected]);

  return (
    <>
      <Header />
      {route.view === "home" && <HomePage />}
      {route.view === "marketplace" && <Marketplace />}
      {route.view === "vehicle" && <VehicleRoute vehicle={selected} />}
      {route.view === "dashboard" && <ProtectedRoute auth={auth} role="customer"><Dashboard /></ProtectedRoute>}
      {route.view === "private-admin" && <AdminProtectedRoute auth={adminAuth}><Admin auth={adminAuth} /></AdminProtectedRoute>}
      {route.view === "admin-auth" && <AdminAuthFlow onAuth={setAdminAuth} />}
      {route.view === "auth" && <AuthFlow auth={auth} onAuth={setAuth} />}
      {route.view === "pending" && <SystemState title="Account pending approval" body="Your account is awaiting admin approval." />}
      {route.view === "not-found" && <SystemState title="Page not found" body="The requested route does not exist." />}
      <Footer />
      <BottomNav />
    </>
  );
}
